import React, { useEffect, useMemo, useState } from 'react';
import {
	Row,
	Col,
	Select,
	Card,
} from 'antd';
import {
	FixtureTypeLabels,
	FixtureTypes,
	TcInAllocationItemTypes,
} from '@shared/utils/constants';
import { sortByDates } from '@shared/utils/sortByDates';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import BunkersTable from '@client/components/BunkersTable/BunkersTable';
import { updateTcInAllocationItem, updateTcTerms } from '@client/lib/api';
import NumericInput from '@client/components/NumericInput';
import ExpensesTable from './ExpensesTable';
import OffHireTable from './OffHireTable';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import VendorExpensesCard from './VendorExpensesCard';
import EuExpenseCard from './EuExpenseCard';

const TcExpensesTab = () => {
	const {
		voyageDetails,
		fixtureCurrency,
		tcFixtureDetails: fixtureDetails,
		refreshDetails,
		allVoyages,
	} = useVoyage();

	const [grossBallastBonus, setGrossBallastBonus] = useState(
		voyageDetails.tcTerms?.grossBallastBonus ?? 0,
	);

	useEffect(() => {
		setGrossBallastBonus(voyageDetails.tcTerms?.grossBallastBonus ?? 0);
	}, [voyageDetails.tcTerms?.grossBallastBonus]);

	const linkedOutVoyages = useMemo(() => (
		allVoyages?.filter((voyage) => (
			voyage.linkedTcInVoyageId === voyageDetails.id
		)) ?? []
	), [allVoyages, voyageDetails.id]);

	const expenses = useMemo(() => {
		if (!voyageDetails?.id) {
			return [];
		}

		const sortedByDate = sortByDates(voyageDetails.voyageExpenseReceivables, 'createdAt');

		return sortedByDate.sort((a, b) => a.account.localeCompare(b.account));
	}, [voyageDetails]);

	const voyageId = voyageDetails.id;

	const grossBallastBonusAllocation = voyageDetails.tcInAllocationItems.find((item) => (
		item.itemType === TcInAllocationItemTypes.GROSS_BALLAST_BONUS
	));

	const selectedGrossBallastBonusVoyageId = grossBallastBonusAllocation?.allocatedToId ?? undefined;

	return (
		<Row gutter={[16, 16]}>
			<Col span={24}>
				<BunkersTable
					bunkers={voyageDetails?.voyageBunkers}
					voyageId={voyageId}
					tcTermsId={voyageDetails.tcTerms?.id}
					refreshDetails={refreshDetails}
					currency={fixtureCurrency}
					availableFuelGrades={voyageDetails.vesselFuelGrades}
				/>
			</Col>
			<Col span={24}>
				<ExpensesTable
					title="Voyage expenses"
					id={voyageId}
					refreshDetails={refreshDetails}
					fixtureCurrency={fixtureCurrency}
					fixtureType={fixtureDetails.type}
					expenses={expenses}
					linkedOutVoyages={linkedOutVoyages ?? []}
					tcInAllocationItems={voyageDetails.tcInAllocationItems}
				/>
			</Col>
			{voyageDetails.fixture.type === FixtureTypes.TC_IN && (
				<Col span={24}>
					<Card
						title="Gross Ballast Bonus"
						size="small"
					>
						<Row gutter={24}>
							<Col span={12}>
								<div style={{ marginBottom: 6 }}>
									Amount
								</div>
								<NumericInput
									addonBefore={fixtureCurrency}
									value={grossBallastBonus}
									style={{ width: '100%' }}
									onChange={(value) => {
										setGrossBallastBonus(value ?? 0); 
									}}
									onBlur={async () => {
										if (voyageDetails.tcTerms?.id == null) {
											return;
										}

										if (grossBallastBonus === (voyageDetails.tcTerms.grossBallastBonus ?? 0)) {
											return;
										}

										await updateTcTerms(voyageDetails.tcTerms.id, { grossBallastBonus } );

										await refreshDetails();
									}}
								/>
							</Col>

							<Col span={12}>
								<div style={{ marginBottom: 6 }}>
									Allocate to voyage
								</div>
								<Select
									allowClear
									placeholder="Select voyage"
									value={selectedGrossBallastBonusVoyageId}
									status={
										selectedGrossBallastBonusVoyageId == null ?
											'error' :
											undefined
									}
									style={{ width: '100%' }}
									options={linkedOutVoyages.map((voyage) => ({
										value: voyage.id,
										label: `${voyage.identifier} - ${FixtureTypeLabels[voyage.fixtureType]}`,
									}))}
									onChange={async (newVoyageId?: number) => {
										if (newVoyageId != null) {
											await updateTcInAllocationItem(
												newVoyageId,
												0,
												TcInAllocationItemTypes.GROSS_BALLAST_BONUS,
												100,
											);
										} else if (selectedGrossBallastBonusVoyageId != null) {
											await updateTcInAllocationItem(
												selectedGrossBallastBonusVoyageId,
												0,
												TcInAllocationItemTypes.GROSS_BALLAST_BONUS,
												0,
											);
										}

										await refreshDetails();
									}}
								/>
							</Col>
						</Row>
					</Card>
				</Col>
			)}
			{voyageDetails.fixture.type !== FixtureTypes.BB_OUT && (
				<Col span={24}>
					<OffHireTable
						id={voyageId}
						details={voyageDetails}
						fixtureDetails={fixtureDetails as GetFixtureDetailsResponse<TcFixtureProps>}
						refreshDetails={refreshDetails}
					/>
				</Col>
			)}
			<Col span={24}>
				<VendorExpensesCard
					allowCreatePortDA={voyageDetails.fixture.type !== FixtureTypes.TC_IN}
				/>
			</Col>
			<Col span={24}>
				<EuExpenseCard />
			</Col>
		</Row>
	);
};

export default TcExpensesTab;
